import React, { useState, useEffect, useMemo } from "react";
import { db, auth } from "../../firebase/firebase.js";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  getDocs,
  deleteDoc, // Added for deleting documents
} from "firebase/firestore";
// doc and onSnapshot were already imported, ensured they are used as per plan.
import { getTodayString, clearTodayData } from "../../utils/todayData.js"; // Added import
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ClearIcon from "@mui/icons-material/Clear"; // Icon for unmarking
import UrgentNodeCard from "./UrgentNodeCard.jsx";
import { updateActivity } from "../dashboard/api/activity.js";

const TodayPage = () => {
  const theme = useTheme();
  const [allActivities, setAllActivities] = useState([]);
  const [pointsTodayActivities, setPointsTodayActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [userId, setUserId] = useState(null);
  const [todayActivitiesMap, setTodayActivitiesMap] = useState({});
  const [maxPoints, setMaxPoints] = useState(1);
  const [editingNodeDetail, setEditingNodeDetail] = useState({
    instanceId: null,
    field: null,
    tempValue: "",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState({
    open: false,
    nodeToDelete: null,
  });

  const [imageGeneratingForNodes, setImageGeneratingForNodes] = useState([]);
  const [imageUploadDialogOpen, setImageUploadDialogOpen] = useState(false);
  const [currentNodeForImageUpload, setCurrentNodeForImageUpload] =
    useState(null);
  const [customImageUrl, setCustomImageUrl] = useState("");
  const [customImageFile, setCustomImageFile] = useState(null);
  // const inputRef = React.useRef(null); // If needed for focusing from this page

  const urgentActivities = useMemo(
    () => allActivities.filter((activity) => activity.isUrgent),
    [allActivities]
  );

  const accentTone = useMemo(
    () =>
      theme.palette.secondary?.main ??
      theme.palette.primary.light ??
      theme.palette.primary.main,
    [theme]
  );

  const pageBackground = useMemo(() => {
    const base =
      theme.palette.mode === "light"
        ? theme.palette.grey[100]
        : theme.palette.background.default;
    const primaryGlow = alpha(
      theme.palette.primary.main,
      theme.palette.mode === "light" ? 0.12 : 0.32
    );
    const accentGlow = alpha(
      accentTone,
      theme.palette.mode === "light" ? 0.1 : 0.26
    );
    return `radial-gradient(circle at 0% -20%, ${primaryGlow} 0%, transparent 55%), radial-gradient(circle at 120% -15%, ${accentGlow} 0%, transparent 52%), ${base}`;
  }, [accentTone, theme]);

  const panelSurface = useMemo(
    () => ({
      position: "relative",
      overflow: "hidden",
      borderRadius: 3,
      background:
        theme.palette.mode === "light"
          ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(accentTone, 0.09)} 120%)`
          : `linear-gradient(135deg, ${alpha(theme.palette.primary.dark ?? theme.palette.primary.main, 0.55)} 0%, ${alpha(accentTone, 0.3)} 120%)`,
      border: `1px solid ${alpha(
        theme.palette.primary.main,
        theme.palette.mode === "light" ? 0.16 : 0.4
      )}`,
      boxShadow: theme.shadows[6],
      backdropFilter: "blur(18px)",
    }),
    [accentTone, theme]
  );

  const sectionHeadingSx = useMemo(
    () => ({
      fontWeight: 700,
      letterSpacing: 0.3,
      color:
        theme.palette.mode === "light"
          ? alpha(theme.palette.text.primary, 0.85)
          : alpha(theme.palette.common.white, 0.9),
    }),
    [theme]
  );

  // Effect to get the current user's ID
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      setUserId(currentUser.uid);
    }
    // Optional: Listen for auth state changes if component can mount before user is known
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null); // Handle user logging out
      }
    });
    return () => unsubscribe(); // Cleanup listener
  }, []);

  // Effect to fetch all activities
  useEffect(() => {
    if (!userId) {
      setLoading(false); // Not loading if no user
      setAllActivities([]); // Clear activities if no user
      return;
    }

    setLoading(true);
    const q = query(collection(db, "progress"), where("uid", "==", userId));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const activities = [];
        querySnapshot.forEach((doc) => {
          activities.push({ id: doc.id, ...doc.data() });
        });
        setAllActivities(activities);
        setLoading(false);
        setErrorMessage("");
      },
      (error) => {
        console.error("Error fetching all activities:", error);
        setLoading(false);
        setErrorMessage(
          "We couldn't load your activities right now. Please try again later."
        );
      }
    );

    return () => unsubscribe(); // Cleanup listener on component unmount or userId changes
  }, [userId]);

  // Effect to listen to the 'today' document
  useEffect(() => {
    if (!userId) {
      setTodayActivitiesMap({});
      return;
    }

    const todayDocRef = doc(db, "today", userId);
    const unsubscribeToday = onSnapshot(
      todayDocRef,
      async (docSnap) => {
        let currentTodayActivitiesMap = {};
        if (docSnap.exists()) {
          const todayData = docSnap.data();
          if (todayData.date === getTodayString()) {
            currentTodayActivitiesMap = todayData.activities || {};
            setTodayActivitiesMap(currentTodayActivitiesMap);
            setErrorMessage("");
          } else {
            await clearTodayData(userId);
            setTodayActivitiesMap({}); // Reset for the new day
            // currentTodayActivitiesMap remains {}
          }
        } else {
          await clearTodayData(userId);
          setTodayActivitiesMap({}); // Reset as no doc exists
          // currentTodayActivitiesMap remains {}
        }

        // Derive pointsTodayActivities from allActivities and currentTodayActivitiesMap
        if (userId && allActivities.length > 0) {
          const derivedPointsActivities = allActivities.filter((activity) => {
            return (
              currentTodayActivitiesMap[activity.id] &&
              currentTodayActivitiesMap[activity.id].points > 0
            );
          });
          setPointsTodayActivities(derivedPointsActivities);
        } else if (userId && allActivities.length === 0) {
          // If user is logged in but allActivities hasn't populated yet,
          // or if there are simply no activities, ensure pointsTodayActivities is empty.
          setPointsTodayActivities([]);
        } else if (!userId) {
          // If no user, clear the list
          setPointsTodayActivities([]);
        }
      },
      (error) => {
        console.error("Error listening to today document:", error);
        setTodayActivitiesMap({}); // Reset on error
        setPointsTodayActivities([]); // Reset on error
        setErrorMessage(
          "We couldn't load today's progress. Please try again later."
        );
      }
    );

    return () => unsubscribeToday(); // Cleanup listener
  }, [userId, allActivities]); // Added allActivities to dependency array

  // Effect to fetch all nodes for maxPoints calculation
  useEffect(() => {
    if (!userId) {
      setMaxPoints(1); // Reset if no user
      return;
    }

    const fetchAllNodes = async () => {
      try {
        const q = query(collection(db, "progress"), where("uid", "==", userId));
        const querySnapshot = await getDocs(q);
        let currentMaxPoints = 0;
        querySnapshot.forEach((doc) => {
          const nodeData = doc.data();
          if (nodeData.title !== "Life" && nodeData.points > currentMaxPoints) {
            currentMaxPoints = nodeData.points;
          }
        });
        setMaxPoints(currentMaxPoints === 0 ? 1 : currentMaxPoints);
      } catch (error) {
        console.error("Error fetching all nodes for maxPoints:", error);
        setMaxPoints(1); // Default on error
      }
    };

    fetchAllNodes();
  }, [userId]);

  const handleUnmarkUrgent = async (activityId) => {
    if (!activityId) {
      console.error("Invalid activityId provided to handleUnmarkUrgent");
      return;
    }
    try {
      const activityRef = doc(db, "progress", activityId);
      await updateDoc(activityRef, {
        isUrgent: false,
      });
      // The onSnapshot listener will automatically update the local state.
    } catch (error) {
      console.error("Error unmarking urgent status:", error);
    }
  };

  const handleToggleUrgent = async (activityId, currentIsUrgent) => {
    if (!activityId) {
      console.error("Invalid activityId provided to handleToggleUrgent");
      return;
    }
    try {
      const activityRef = doc(db, "progress", activityId);
      await updateDoc(activityRef, {
        isUrgent: !currentIsUrgent, // Toggle the status
      });
      // The onSnapshot listener for urgentActivities will update it.
      // For pointsTodayActivities, if an activity's urgent status changes,
      // it will remain in or be removed from that list based on points, not urgency alone.
    } catch (error) {
      console.error("Error toggling urgent status:", error);
    }
  };

  const handleEditOpen = (node, field, sectionPrefix) => {
    // Ensure node[field] is not undefined, provide a default if necessary, e.g., for a new unit.
    const currentValue = node[field] !== undefined ? node[field] : "";
    setEditingNodeDetail({
      instanceId: `${sectionPrefix}-${node.id}`,
      field: field,
      tempValue: currentValue,
    });
  };

  const handleEditChange = (event) => {
    setEditingNodeDetail((prev) => ({
      ...prev,
      tempValue: event.target.value,
    }));
  };

  const handleEditSubmit = async () => {
    if (editingNodeDetail.instanceId && editingNodeDetail.field) {
      // Extract the actual nodeId from instanceId
      const nodeId = editingNodeDetail.instanceId.split("-").slice(1).join("-");
      const { field, tempValue } = editingNodeDetail;
      let valueToUpdate = tempValue;

      if (field === "dailyPoints") {
        valueToUpdate = Number(tempValue);
        if (isNaN(valueToUpdate) || valueToUpdate < 0) {
          console.error("Daily points must be a non-negative number");
          // Optionally, provide user feedback here via a snackbar or alert
          setEditingNodeDetail({
            instanceId: null,
            field: null,
            tempValue: "",
          }); // Reset on error
          return;
        }
      }

      const updateObject = { [field]: valueToUpdate };
      try {
        const nodeRef = doc(db, "progress", nodeId);
        await updateDoc(nodeRef, updateObject);
        setEditingNodeDetail({ instanceId: null, field: null, tempValue: "" });
      } catch (error) {
        console.error("Error updating node:", error);
        // Optionally, revert tempValue or show error to user
      }
    }
  };

  const handleEditCancel = () => {
    setEditingNodeDetail({ instanceId: null, field: null, tempValue: "" });
  };

  // This specific handleKeyPress is for inline text fields within the card
  // It's called by UrgentNodeCard's TextField's onKeyDown
  const handleItemKeyPress = (event, node, field) => {
    if (event.key === "Enter") {
      // node and field might be part of editingNodeDetail, or passed if structure is different
      handleEditSubmit();
    } else if (event.key === "Escape") {
      handleEditCancel();
    }
  };

  const handleFocus = (event) => {
    event.target.select();
  };

  const updateNodePoints = async (node, increment) => {
    const newPoints = (node.points || 0) + increment;
    const updateData = { points: newPoints };

    // If increment is 0, 'today' is not changed by this logic.

    try {
      const nodeRef = doc(db, "progress", node.id);
      await updateDoc(nodeRef, updateData);
      await updateActivity(node, increment); // Added call to updateActivity
    } catch (error) {
      console.error("Error updating points or activity:", error); // Modified error message
    }
  };

  // For UrgentNodeCard, if it has a specific "decrement" button that needs confirmation or special logic.
  // If it's just a point update, updateNodePoints(node, -1) is fine.
  // This handler is for the case where UrgentNodeCard's "CloseIcon" button is clicked.
  const handleDecrementOpen = (node) => {
    // For now, directly decrement. If a dialog or specific logic is needed, implement here.
    updateNodePoints(node, -1);
  };

  const handleDeleteOpen = (node) => {
    setShowDeleteConfirm({ open: true, nodeToDelete: node });
  };

  const handleDeleteClose = () => {
    setShowDeleteConfirm({ open: false, nodeToDelete: null });
  };

  const handleDeleteConfirm = async () => {
    if (showDeleteConfirm.nodeToDelete && showDeleteConfirm.nodeToDelete.id) {
      try {
        const nodeId = showDeleteConfirm.nodeToDelete.id;
        await deleteDoc(doc(db, "progress", nodeId));
        // No need to manually filter urgentActivities, onSnapshot will do it.
        handleDeleteClose();
      } catch (error) {
        console.error("Error deleting node:", error);
        // Consider user feedback for error
      }
    } else {
      console.error("No node to delete or node ID missing.");
      handleDeleteClose(); // Still close dialog
    }
  };

  // Corrected regenerateImageForTitle function
  const regenerateImageForTitle = (node) => {
    // Takes node object
    if (!node || !node.id || !node.title) {
      console.error(
        "Invalid node data passed to regenerateImageForTitle:",
        node
      );
      return;
    }
    console.log(
      `Image regeneration requested for node ID: ${node.id}, title: ${node.title}`
    );
    setImageGeneratingForNodes((prev) => [...prev, node.id]);
    // Placeholder for AI Image Generation Service Call
    setTimeout(async () => {
      try {
        const placeholderUrl = `https://via.placeholder.com/200/0000FF/808080?Text=Generated+${node.title.replace(
          /\s+/g,
          "+"
        )}`;
        const nodeRef = doc(db, "progress", node.id);
        await updateDoc(nodeRef, { image: placeholderUrl });
      } catch (error) {
        console.error("Error updating node with generated image:", error);
      } finally {
        setImageGeneratingForNodes((prev) =>
          prev.filter((id) => id !== node.id)
        );
      }
    }, 3000);
  };

  // The saveCustomImageToGCS function that contained duplicated logic is removed.
  // The prop saveCustomImageToGCS on the card is correctly wired to handleOpenImageUploadDialog.

  const handleOpenImageUploadDialog = (node) => {
    setCurrentNodeForImageUpload(node);
    setCustomImageUrl("");
    setCustomImageFile(null);
    setImageUploadDialogOpen(true);
  };

  const handleCloseImageUploadDialog = () => {
    setImageUploadDialogOpen(false);
    setCurrentNodeForImageUpload(null);
    setCustomImageUrl("");
    setCustomImageFile(null);
  };

  const handleCustomImageUrlChange = (event) => {
    setCustomImageUrl(event.target.value);
  };

  const handleCustomImageFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setCustomImageFile(event.target.files[0]);
    }
  };

  const handleImageUploadSubmit = async () => {
    if (!currentNodeForImageUpload) return;

    setImageGeneratingForNodes((prev) => [
      ...prev,
      currentNodeForImageUpload.id,
    ]);
    handleCloseImageUploadDialog(); // Close dialog immediately

    let newImageUrl = "";

    try {
      if (customImageFile) {
        // Placeholder for actual file upload
        newImageUrl = `https://via.placeholder.com/200/00FF00/808080?Text=Uploaded+${customImageFile.name.replace(
          /\s+/g,
          "+"
        )}`;
        // Simulate upload delay
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } else if (customImageUrl) {
        // Basic URL validation (optional, enhance as needed)
        if (
          !customImageUrl.startsWith("http://") &&
          !customImageUrl.startsWith("https://")
        ) {
          console.error("Invalid image URL provided.");
          // Potentially show error to user
          setImageGeneratingForNodes((prev) =>
            prev.filter((id) => id !== currentNodeForImageUpload.id)
          );
          return;
        }
        newImageUrl = customImageUrl;
      }

      if (newImageUrl) {
        const nodeRef = doc(db, "progress", currentNodeForImageUpload.id);
        await updateDoc(nodeRef, { image: newImageUrl });
      }
    } catch (error) {
      console.error("Error processing image upload:", error);
    } finally {
      setImageGeneratingForNodes((prev) =>
        prev.filter((id) => id !== currentNodeForImageUpload.id)
      );
    }
  };

  // Placeholder for nodes that should be visually highlighted (e.g., after a points update)
  // This would also be managed by state.
  const highlightedNodes = {}; // Example: { 'nodeId1': 'gain', 'nodeId2': 'lose' }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100%",
          py: { xs: 2.5, md: 4 },
          px: { xs: 2, md: 4 },
          background: pageBackground,
          transition: "background 320ms ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress thickness={5} />
      </Box>
    );
  }

  const urgentCount = urgentActivities.length;
  const pointsCount = pointsTodayActivities.length;

  return (
    <Box
      sx={{
        minHeight: "100%",
        py: { xs: 2.5, md: 4 },
        px: { xs: 2, md: 4 },
        background: pageBackground,
        transition: "background 320ms ease",
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...panelSurface,
            px: { xs: 2.75, md: 3.75 },
            py: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2.5, md: 3.5 },
            alignItems: { xs: "flex-start", md: "center" },
            background:
              theme.palette.mode === "light"
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(accentTone, 0.12)} 38%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.42)} 0%, ${alpha(accentTone, 0.3)} 40%, ${alpha(theme.palette.background.paper, 0.78)} 100%)`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background:
                theme.palette.mode === "light"
                  ? `radial-gradient(circle at 18% 25%, ${alpha(theme.palette.common.white, 0.5)} 0%, transparent 55%)`
                  : `radial-gradient(circle at 18% 25%, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.28)} 0%, transparent 60%)`,
              opacity: 0.9,
            }}
          />
          <Box
            sx={{
              position: "relative",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Typography variant="overline" sx={{ letterSpacing: 1.6, opacity: 0.72 }}>
              Today's Focus
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }} data-testid="today-urgent-activities-title">
              Urgent Activities
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: 640,
                color:
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.text.primary, 0.78)
                    : alpha(theme.palette.common.white, 0.9),
              }}
            >
              Prioritise the actions that move the needle today. Update progress, adjust targets, or capture quick notes without leaving this view.
            </Typography>
          </Box>
          <Stack sx={{ position: "relative", gap: 1, flexShrink: 0, minWidth: { md: 200 } }}>
            <Chip
              label={`${urgentCount} urgent`}
              size="small"
              sx={{
                fontWeight: 600,
                letterSpacing: 0.3,
                backgroundColor: alpha(accentTone, 0.16),
                color:
                  theme.palette.mode === "light"
                    ? theme.palette.primary.main
                    : alpha(theme.palette.common.white, 0.92),
              }}
            />
            <Chip
              label={`${pointsCount} with points today`}
              size="small"
              sx={{
                fontWeight: 600,
                letterSpacing: 0.3,
                backgroundColor: alpha(theme.palette.success.main, 0.16),
                color:
                  theme.palette.mode === "light"
                    ? theme.palette.success.dark
                    : alpha(theme.palette.common.white, 0.9),
              }}
            />
          </Stack>
        </Paper>

        {errorMessage && (
          <Alert
            severity="error"
            sx={{
              mt: -1,
              mx: "auto",
              width: "100%",
              maxWidth: 720,
              boxShadow: theme.shadows[4],
              borderRadius: 2,
            }}
            onClose={() => setErrorMessage("")}
          >
            {errorMessage}
          </Alert>
        )}

        <Paper elevation={0} sx={{ ...panelSurface, px: { xs: 2.5, md: 3 }, py: { xs: 2.5, md: 3 } }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h6" sx={sectionHeadingSx}>
                Urgent Activities
              </Typography>
              <Chip
                label={urgentCount ? `${urgentCount} active` : "None"}
                size="small"
                sx={{ backgroundColor: alpha(accentTone, 0.18), fontWeight: 600 }}
              />
            </Stack>
            {urgentActivities.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color:
                    theme.palette.mode === "light"
                      ? alpha(theme.palette.text.secondary, 0.8)
                      : alpha(theme.palette.common.white, 0.75),
                }}
              >
                No urgent activities at the moment. Mark a habit or outcome as urgent to keep it front and centre here.
              </Typography>
            ) : (
              <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                {urgentActivities.map((activity) => {
                  const currentActivityTodayPoints =
                    todayActivitiesMap[activity.id]?.points || 0;
                  const dayPercentage = Math.round(
                    Math.min(
                      currentActivityTodayPoints / (activity.dailyPoints || 10),
                      1
                    ) * 100
                  );
                  const colorValue = Math.min(
                    255,
                    Math.max(0, (activity.points / maxPoints) * 255)
                  );
                  const nodeColor = `rgb(${255 - colorValue}, ${colorValue}, 0)`;

                  const instanceKey = `urgent-${activity.id}`;
                  const isEditingCurrentNodeField = (field) =>
                    editingNodeDetail.instanceId === instanceKey &&
                    editingNodeDetail.field === field;

                  const activityWithCorrectTodayPoints = {
                    ...activity,
                    today: currentActivityTodayPoints,
                  };

                  const cardProps = {
                    node: activityWithCorrectTodayPoints,
                    dayPercentage,
                    nodeColor,
                    isUrgent: true,
                    onToggleUrgent: () => handleUnmarkUrgent(activity.id),
                    editingNode:
                      editingNodeDetail.instanceId === instanceKey
                        ? {
                            ...activityWithCorrectTodayPoints,
                            title:
                              editingNodeDetail.field === "title"
                                ? editingNodeDetail.tempValue
                                : activityWithCorrectTodayPoints.title,
                            dailyPoints:
                              editingNodeDetail.field === "dailyPoints"
                                ? editingNodeDetail.tempValue
                                : activityWithCorrectTodayPoints.dailyPoints,
                            unit:
                              editingNodeDetail.field === "unit"
                                ? editingNodeDetail.tempValue
                                : activityWithCorrectTodayPoints.unit,
                          }
                        : null,
                    editingTitle: isEditingCurrentNodeField("title"),
                    editingDailyPoints: isEditingCurrentNodeField("dailyPoints"),
                    editingUnit: isEditingCurrentNodeField("unit"),
                    setEditingNode: (node) => handleEditOpen(node, "title", "urgent"),
                    setEditingTitle: () =>
                      handleEditOpen(
                        activityWithCorrectTodayPoints,
                        "title",
                        "urgent"
                      ),
                    setEditingDailyPoints: () =>
                      handleEditOpen(
                        activityWithCorrectTodayPoints,
                        "dailyPoints",
                        "urgent"
                      ),
                    setEditingUnit: () =>
                      handleEditOpen(
                        activityWithCorrectTodayPoints,
                        "unit",
                        "urgent"
                      ),
                    handleTitleChange: handleEditChange,
                    handleDailyPointsChange: handleEditChange,
                    handleUnitChange: handleEditChange,
                    handleTitleSubmit: handleEditSubmit,
                    handleDailyPointsSubmit: handleEditSubmit,
                    handleUnitSubmit: handleEditSubmit,
                    handleKeyPress: (event, nodeFromCard, fieldFromCard) =>
                      handleItemKeyPress(event, nodeFromCard, fieldFromCard),
                    handleFocus,
                    updateNodePoints: (node, increment) =>
                      updateNodePoints(node, increment),
                    handleDecrementOpen: (node) => handleDecrementOpen(node),
                    handleDeleteOpen: () =>
                      handleDeleteOpen(activityWithCorrectTodayPoints),
                    showImageButtons: true,
                    showImageActionButtons: true,
                    regenerateImageForTitle: () =>
                      regenerateImageForTitle(activityWithCorrectTodayPoints),
                    saveCustomImageToGCS: () =>
                      handleOpenImageUploadDialog(activityWithCorrectTodayPoints),
                    imageGeneratingForNodes,
                    highlightedNodes,
                    addNewChild: () =>
                      console.log(
                        "Add new child requested for:",
                        activityWithCorrectTodayPoints.title
                      ),
                    moveNodeUp: () =>
                      console.log(
                        "Move up requested for:",
                        activityWithCorrectTodayPoints.title
                      ),
                    moveNodeDown: () =>
                      console.log(
                        "Move down requested for:",
                        activityWithCorrectTodayPoints.title
                      ),
                    inputRef: null,
                    newNodeId: null,
                  };

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={4}
                      key={activityWithCorrectTodayPoints.id}
                    >
                      <UrgentNodeCard {...cardProps} />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ ...panelSurface, px: { xs: 2.5, md: 3 }, py: { xs: 2.5, md: 3 } }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography
                variant="h6"
                sx={sectionHeadingSx}
                data-testid="today-points-activities-title"
              >
                Activities with Points Today
              </Typography>
              <Chip
                label={pointsCount ? `${pointsCount} logged` : "No points yet"}
                size="small"
                sx={{ backgroundColor: alpha(theme.palette.success.main, 0.16), fontWeight: 600 }}
              />
            </Stack>
            {pointsTodayActivities.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color:
                    theme.palette.mode === "light"
                      ? alpha(theme.palette.text.secondary, 0.8)
                      : alpha(theme.palette.common.white, 0.75),
                }}
              >
                No activities have earned points today. Log progress on any habit or outcome to see it reflected here.
              </Typography>
            ) : (
              <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                {pointsTodayActivities.map((activity) => {
                  const currentActivityTodayPoints =
                    todayActivitiesMap[activity.id]?.points || 0;
                  const dayPercentage = Math.round(
                    Math.min(
                      currentActivityTodayPoints / (activity.dailyPoints || 10),
                      1
                    ) * 100
                  );
                  const colorValue = Math.min(
                    255,
                    Math.max(0, (activity.points / maxPoints) * 255)
                  );
                  const nodeColor = `rgb(${255 - colorValue}, ${colorValue}, 0)`;

                  const instanceKey = `points-${activity.id}`;
                  const isEditingCurrentNodeField = (field) =>
                    editingNodeDetail.instanceId === instanceKey &&
                    editingNodeDetail.field === field;

                  const activityWithCorrectTodayPoints = {
                    ...activity,
                    today: currentActivityTodayPoints,
                  };

                  const cardProps = {
                    node: activityWithCorrectTodayPoints,
                    dayPercentage,
                    nodeColor,
                    isUrgent: activity.isUrgent || false,
                    onToggleUrgent: () =>
                      handleToggleUrgent(activity.id, activity.isUrgent || false),
                    editingNode:
                      editingNodeDetail.instanceId === instanceKey
                        ? {
                            ...activityWithCorrectTodayPoints,
                            title:
                              editingNodeDetail.field === "title"
                                ? editingNodeDetail.tempValue
                                : activityWithCorrectTodayPoints.title,
                            dailyPoints:
                              editingNodeDetail.field === "dailyPoints"
                                ? editingNodeDetail.tempValue
                                : activityWithCorrectTodayPoints.dailyPoints,
                            unit:
                              editingNodeDetail.field === "unit"
                                ? editingNodeDetail.tempValue
                                : activityWithCorrectTodayPoints.unit,
                          }
                        : null,
                    editingTitle: isEditingCurrentNodeField("title"),
                    editingDailyPoints: isEditingCurrentNodeField("dailyPoints"),
                    editingUnit: isEditingCurrentNodeField("unit"),
                    setEditingNode: (node) => handleEditOpen(node, "title", "points"),
                    setEditingTitle: () =>
                      handleEditOpen(
                        activityWithCorrectTodayPoints,
                        "title",
                        "points"
                      ),
                    setEditingDailyPoints: () =>
                      handleEditOpen(
                        activityWithCorrectTodayPoints,
                        "dailyPoints",
                        "points"
                      ),
                    setEditingUnit: () =>
                      handleEditOpen(
                        activityWithCorrectTodayPoints,
                        "unit",
                        "points"
                      ),
                    handleTitleChange: handleEditChange,
                    handleDailyPointsChange: handleEditChange,
                    handleUnitChange: handleEditChange,
                    handleTitleSubmit: handleEditSubmit,
                    handleDailyPointsSubmit: handleEditSubmit,
                    handleUnitSubmit: handleEditSubmit,
                    handleKeyPress: (event, nodeFromCard, fieldFromCard) =>
                      handleItemKeyPress(event, nodeFromCard, fieldFromCard),
                    handleFocus,
                    updateNodePoints: (node, increment) =>
                      updateNodePoints(node, increment),
                    handleDecrementOpen: (node) => handleDecrementOpen(node),
                    handleDeleteOpen: () =>
                      handleDeleteOpen(activityWithCorrectTodayPoints),
                    showImageButtons: true,
                    showImageActionButtons: true,
                    regenerateImageForTitle: () =>
                      regenerateImageForTitle(activityWithCorrectTodayPoints),
                    saveCustomImageToGCS: () =>
                      handleOpenImageUploadDialog(activityWithCorrectTodayPoints),
                    imageGeneratingForNodes,
                    highlightedNodes,
                    addNewChild: () =>
                      console.log(
                        "Add new child requested for:",
                        activityWithCorrectTodayPoints.title
                      ),
                    moveNodeUp: () =>
                      console.log(
                        "Move up requested for:",
                        activityWithCorrectTodayPoints.title
                      ),
                    moveNodeDown: () =>
                      console.log(
                        "Move down requested for:",
                        activityWithCorrectTodayPoints.title
                      ),
                    inputRef: null,
                    newNodeId: null,
                  };

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={4}
                      key={activity.id}
                    >
                      <UrgentNodeCard {...cardProps} />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Stack>
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm.open}
        onClose={handleDeleteClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the activity "
            {showDeleteConfirm.nodeToDelete?.title}"? This action cannot be
            undone.
            {/* Ensure nodeToDelete?.title still works as expected with activityWithCorrectTodayPoints */}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Image Upload Dialog */}
      <Dialog
        open={imageUploadDialogOpen}
        onClose={handleCloseImageUploadDialog}
      >
        <DialogTitle>
          Upload Custom Image for {currentNodeForImageUpload?.title}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="image-url"
            label="Image URL"
            type="text"
            fullWidth
            variant="standard"
            value={customImageUrl}
            onChange={handleCustomImageUrlChange}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Or Upload File:
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={handleCustomImageFileChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageUploadDialog}>Cancel</Button>
          <Button
            onClick={handleImageUploadSubmit}
            disabled={!customImageUrl && !customImageFile}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TodayPage;
